import { Link, useNavigate } from 'react-router-dom';

/**
 * Tier picker — `/tier`. Three cards (Explorers / Builders / Designers).
 *
 * Explorers links to `/` (the kid canvas). Builders and Designers are
 * Phase 2 / Phase 4 work and currently route to placeholder "Coming
 * soon" pages so the URL space exists when those tiers ship.
 *
 * Last-chosen persistence is intentionally NOT keyed off this picker
 * yet — `/` always lands on Explorers at MVP since that's the only
 * tier with a real canvas. When Builders ships, hydrate this from
 * localStorage and have `/` redirect to the correct tier path.
 */
const TIERS = [
  {
    id: 'explorers',
    title: 'Explorers',
    age: 'Ages 5–7',
    emoji: '🧒',
    blurb: 'Big shapes, tap-to-build, friendly mascot. The first 3D adventure.',
    href: '/',
    available: true,
  },
  {
    id: 'builders',
    title: 'Builders',
    age: 'Ages 8–10',
    emoji: '🛠️',
    blurb: 'Multi-shape, holes, rotations. Build something real and print it.',
    href: '/builders',
    available: false,
  },
  {
    id: 'designers',
    title: 'Inventors',
    age: 'Ages 11–12',
    emoji: '🚀',
    blurb: 'Free-form shapes, parametric dimensions, real engineering moves.',
    href: '/designers',
    available: false,
  },
] as const;

export function TierPicker(): JSX.Element {
  const navigate = useNavigate();

  return (
    <div className="min-h-dvh bg-amber-50 px-4 py-6 sm:py-12">
      <div className="mx-auto max-w-4xl">
        <header className="mb-8 text-center">
          <h1 className="font-bold text-3xl text-slate-900 sm:text-4xl">Pick your tier</h1>
          <p className="mt-2 text-base text-slate-600">
            Three different paths. Choose the one that fits the age + skill level.
          </p>
        </header>

        <ul className="grid gap-4 sm:grid-cols-3">
          {TIERS.map((tier) => (
            <li key={tier.id}>
              <button
                type="button"
                onClick={() => navigate(tier.href)}
                disabled={!tier.available}
                className={`flex h-full w-full flex-col items-center gap-3 rounded-3xl border-2 p-6 text-center transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 ${
                  tier.available
                    ? 'border-slate-200 bg-white hover:border-amber-300'
                    : 'border-slate-200 bg-white'
                }`}
              >
                <span aria-hidden="true" className="text-6xl leading-none">
                  {tier.emoji}
                </span>
                <span className="font-semibold text-slate-900 text-xl">{tier.title}</span>
                <span className="text-slate-500 text-sm">{tier.age}</span>
                <span className="text-slate-700 text-sm">{tier.blurb}</span>
                {!tier.available && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-600 text-xs">
                    Coming soon
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>

        <p className="mt-8 text-center text-slate-500 text-sm">
          <Link to="/" className="underline-offset-2 hover:underline">
            Back to canvas
          </Link>
        </p>
      </div>
    </div>
  );
}
